/**
 * MasterDataCounterPartyBankFormDialog — Create/Edit `cp_bank`.
 * 1:1 port of Access frmMdQryCPBankAdd (Master Data > CP List > CP Bank).
 *
 * Field order theo Access TabIndex: CP Code (combo vendor) → CP Name (readonly,
 * auto từ vendor) → Bank (combo) → Bank Account → Branch → Account name → Note.
 * PK (cp_code / bank / bank_account) bất biến sau khi tạo → readonly khi Edit.
 * Validate btnSave: CP Code / Bank / Bank Account bắt buộc; trùng khoá → BE 400.
 */
import {
  Autocomplete,
  createFilterOptions,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useListBanksQuery } from '@/store/api/purchaseContractApi'
import { useListMdCounterPartiesQuery } from '@/store/api/masterDataCrudApi'
import {
  useCreateMdCounterPartyBankMutation,
  useUpdateMdCounterPartyBankMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataCounterPartyBank } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataCounterPartyBank | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  cp_code: string
  cp_name: string
  bank: string
  bank_account: string
  branch: string
  account_name: string
  note: string
}

const EMPTY: FormState = {
  cp_code: '',
  cp_name: '',
  bank: '',
  bank_account: '',
  branch: '',
  account_name: '',
  note: '',
}

// cp_code combo (Access ColumnCount=2 code+name): dropdown hiện "code — name",
// nhưng ô input SAU khi chọn chỉ hiện code (bound column) — khớp Access. Filter
// vẫn match cả code lẫn name để dễ tìm.
const vendorFilter = createFilterOptions<{ code: string; name: string }>({
  stringify: (o) => `${o.code} ${o.name}`,
})

export default function MasterDataCounterPartyBankFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createCounterPartyBank, { isLoading: creating }] = useCreateMdCounterPartyBankMutation()
  const [updateCounterPartyBank, { isLoading: updating }] = useUpdateMdCounterPartyBankMutation()
  const loading = creating || updating

  // Dropdown sources — chỉ nạp khi dialog mở.
  const { data: vendorsData } = useListMdCounterPartiesQuery(
    { page: 1, page_size: 1_000_000 },
    { skip: !open },
  )
  const { data: banksData } = useListBanksQuery(
    { page: 1, page_size: 1_000_000 },
    { skip: !open },
  )

  const vendorOptions = useMemo(
    () =>
      (vendorsData?.items ?? []).map((v) => ({
        code: v.code,
        name: v.name ?? '',
      })),
    [vendorsData],
  )
  // Access bank combo: RowSource chỉ `public_bank.bank` (ColumnCount=1) → hiện
  // đúng bank code (KHÔNG kèm tên) ở cả dropdown lẫn input. BankOut trả field
  // `bank` (không phải `bank_code`) → đọc `bk.bank`; lọc rỗng tránh key trùng ''.
  const bankOptions = useMemo(
    () =>
      (banksData?.items ?? [])
        .map((bk) => ({ code: bk.bank ?? bk.bank_code ?? '' }))
        .filter((o) => o.code),
    [banksData],
  )

  const [form, setForm] = useState<FormState>(EMPTY)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setTouched(false)
    if (isEdit && initialValue) {
      setForm({
        cp_code: initialValue.cp_code ?? '',
        cp_name: initialValue.name ?? '',
        bank: initialValue.bank ?? '',
        bank_account: initialValue.bank_account ?? '',
        branch: initialValue.branch ?? '',
        account_name: initialValue.account_name ?? '',
        note: initialValue.note ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  // cp_code_AfterUpdate: CP Name = column(1) của vendor đang chọn.
  const pickVendor = (code: string) => {
    const v = vendorOptions.find((o) => o.code === code)
    setForm((p) => ({ ...p, cp_code: code, cp_name: v?.name ?? '' }))
  }

  const cpEmpty = !form.cp_code.trim()
  const bankEmpty = !form.bank.trim()
  const accEmpty = !form.bank_account.trim()
  const canSave = isEdit || (!cpEmpty && !bankEmpty && !accEmpty)

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (!isEdit && (cpEmpty || bankEmpty || accEmpty)) {
      setTouched(true)
      return
    }
    if (loading) return
    try {
      if (isEdit && initialValue) {
        await updateCounterPartyBank({
          bank_account: initialValue.bank_account,
          cp_code: initialValue.cp_code,
          bank: initialValue.bank,
          body: {
            branch: form.branch.trim() || null,
            account_name: form.account_name.trim() || null,
            note: form.note.trim() || null,
          },
        }).unwrap()
        snack.success('Counter Party Bank updated')
      } else {
        await createCounterPartyBank({
          bank_account: form.bank_account.trim(),
          cp_code: form.cp_code.trim(),
          bank: form.bank.trim(),
          branch: form.branch.trim() || null,
          account_name: form.account_name.trim() || null,
          note: form.note.trim() || null,
        }).unwrap()
        snack.success('Counter Party Bank created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  const cpValue = vendorOptions.find((o) => o.code === form.cp_code) ?? null
  const bankValue = bankOptions.find((o) => o.code === form.bank) ?? null

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
          {isEdit ? 'Edit Counter Party Bank' : 'New Counter Party Bank'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.cp_code} / {initialValue.bank} / {initialValue.bank_account}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave || loading}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', rowGap: 1.5, mt: 0.5 }}>
          {/* Counter Party Code — combo vendor (LimitToList). Readonly khi Edit (PK). */}
          <Autocomplete
            size="small"
            fullWidth
            disabled={isEdit}
            options={vendorOptions}
            value={cpValue}
            onChange={(_e, v) => pickVendor(v?.code ?? '')}
            getOptionLabel={(o) => o?.code ?? ''}
            filterOptions={vendorFilter}
            renderOption={(props, o) => (
              <li {...props} key={o.code}>{o.name ? `${o.code} — ${o.name}` : o.code}</li>
            )}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Counter Party Code"
                required
                error={!isEdit && touched && cpEmpty}
                InputLabelProps={{ shrink: true }}
                inputProps={{ ...params.inputProps, tabIndex: isEdit ? -1 : 1 }}
                sx={isEdit ? roSx : undefined}
                helperText={isEdit ? 'Cannot be changed after creation.' : undefined}
              />
            )}
          />
          {/* Counter Party Name — readonly, derived from vendor. */}
          <TextField
            label="Counter Party Name"
            size="small"
            fullWidth
            value={form.cp_name}
            InputLabelProps={{ shrink: true }}
            InputProps={{ readOnly: true }}
            inputProps={{ tabIndex: -1 }}
            sx={roSx}
          />
          {/* Bank — combo (LimitToList). Readonly khi Edit (PK). */}
          <Autocomplete
            size="small"
            fullWidth
            disabled={isEdit}
            options={bankOptions}
            value={bankValue}
            onChange={(_e, v) => set('bank', v?.code ?? '')}
            getOptionLabel={(o) => o?.code ?? ''}
            isOptionEqualToValue={(a, b) => a.code === b.code}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Bank"
                required
                error={!isEdit && touched && bankEmpty}
                InputLabelProps={{ shrink: true }}
                inputProps={{ ...params.inputProps, tabIndex: isEdit ? -1 : 2 }}
                sx={isEdit ? roSx : undefined}
                helperText={isEdit ? 'Cannot be changed after creation.' : undefined}
              />
            )}
          />
          {/* Bank Account — PK, readonly khi Edit. */}
          <TextField
            label="Bank Account"
            required
            size="small"
            fullWidth
            value={form.bank_account}
            onChange={(e) => set('bank_account', e.target.value)}
            disabled={isEdit}
            error={!isEdit && touched && accEmpty}
            InputLabelProps={{ shrink: true }}
            InputProps={isEdit ? { readOnly: true } : undefined}
            inputProps={{ tabIndex: isEdit ? -1 : 3 }}
            sx={isEdit ? roSx : undefined}
            helperText={isEdit ? 'Cannot be changed after creation.' : undefined}
          />
          <TextField
            label="Branch"
            size="small"
            fullWidth
            value={form.branch}
            onChange={(e) => set('branch', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: 4 }}
          />
          <TextField
            label="Account name"
            size="small"
            fullWidth
            value={form.account_name}
            onChange={(e) => set('account_name', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: 5 }}
          />
          <TextField
            label="Note"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={form.note}
            onChange={(e) => set('note', e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: 6 }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}
